/*
	bf_helper.ts
  Copyright (c) 2013-present Human Cube Inc.
	Helper functions for BF games.
*/

import BF from './bfconst1';
import { Game } from './gameTypes';
import bfTurnReplay from './internal/bf_turnreplay';


function twoDigits (n: number): string {
  // Zero pad to two digits.
  return (n > 9 ? '' : '0') + n;
}

function isLand (game: Game, zoneID: number) {
  // Returns true if zone is land, else false.
  if( zoneID <= 0 || zoneID >= game.zoneFlags.length )
    return false;
  if( game.zoneFlags[zoneID] & BF.ZONE_FLAG_LAND_ZONE )
    return true;
  return false;
}


const bfH = {

  twoDigits,
  isLand,

  isObject: function (v: any) {
    // Returns true if it is an object variable (and not null).
    return (v && typeof v === 'object');
  },

  isString: function (v: any) {
    // Returns true v is a string, else false.
    return (typeof v === 'string' || v instanceof String);
  },

  convertToInt: function (v: any) {
    // Safely convert any value to an integer.  0 for undefined and 0 for NaN.
    v = parseInt(v);
    if(!v)
      v = 0;
    return v;
  },

  isWater: function (game: Game, zoneID: number) {
    // Returns true if zone is water, else false.
    if( zoneID <= 0 || zoneID >= game.zoneFlags.length )
      return false;
    if( game.zoneFlags[zoneID] & BF.ZONE_FLAG_WATER_ZONE )
      return true;
    return false;
  },

  isIsland: function (game: Game, zoneID: number) {
    // Returns true if zone is an island (is land and no land borders), else false.
    if(zoneID <= 0 || zoneID >= game.zoneFlags.length) {
      return false;
    }
    if(game.zoneFlags[zoneID] & BF.ZONE_FLAG_LAND_ZONE) {   // Is land.
      if(game.bordersLand[zoneID].length === 0) {   // No land borders.
        return true;
      }
    }
    return false;
  },

  isLandNeighbour: function (game: Game, zoneID1: number, zoneID2: number) {
    // Returns true if zoneID1 has a land border to zoneID2.
    if(!zoneID1 || !zoneID2 || zoneID1 <= 0 || zoneID1 >= game.bordersLand.length ||
        zoneID2 <= 0 || zoneID2 > game.zones)
      return false;
    const len = game.bordersLand[zoneID1].length;
    for(let i = 0; i < len; i++)
      if(game.bordersLand[zoneID1][i] === zoneID2)
        return true;
    return false;
  },

  isWaterNeighbour: function (game: Game, zoneID1: number, zoneID2: number) {
    // Returns true if zoneID1 has a water border to zoneID2.
    if(!zoneID1 || !zoneID2 || zoneID1 <= 0 || zoneID1 >= game.bordersWater.length ||
        zoneID2 <= 0 || zoneID2 > game.zones )
      return false;
    const len = game.bordersWater[zoneID1].length;
    for(let i = 0; i < len; i++)
      if( game.bordersWater[zoneID1][i] === zoneID2 )
        return true;
    return false;
  },

  isNeighbour: function (game: Game, zoneID1: number, zoneID2: number) {
    // Returns true if zoneID1 has a land or water border to zoneID2.
    if(!zoneID1 || !zoneID2 || zoneID1 <= 0 ||
        zoneID1 >= game.bordersLand.length || zoneID1 >= game.bordersWater.length ||
        zoneID2 <= 0 || zoneID2 > game.zones)
      return false;

    let len = game.bordersLand[zoneID1].length;
    for(let i = 0; i < len; i++)
      if( game.bordersLand[zoneID1][i] === zoneID2 )
        return true;

    len = game.bordersWater[zoneID1].length;
    for(let i = 0; i < len; i++)
      if( game.bordersWater[zoneID1][i] === zoneID2 )
        return true;
    return false;
  },

  randomLandNeighbour: function (game: Game, zoneID: number) {
    // Returns the zoneID of a random land neighbour, or 0 on error.
    if(!zoneID || zoneID <= 0 || zoneID >= game.bordersLand.length ||
        game.bordersLand[zoneID].length === 0)
      return 0;
    const i = Math.floor(Math.random() * game.bordersLand[zoneID].length);
    return game.bordersLand[zoneID][i];
  },

  randomWaterNeighbour: function (game: Game, zoneID: number) {
    // Returns the zoneID of a random water neighbour, or 0 on error.
    if(!zoneID || zoneID <= 0 || zoneID >= game.bordersWater.length ||
        game.bordersWater[zoneID].length === 0)
      return 0;
    const i = Math.floor(Math.random() * game.bordersWater[zoneID].length);
    return game.bordersWater[zoneID][i];
  },

  randomNeighbour: function (game: Game, zoneID: number) {
    // Returns the zoneID of a random neighbour, or 0 on error.
    if(!zoneID || zoneID <= 0 ||
        zoneID >= game.bordersLand.length || zoneID >= game.bordersWater.length) {
      return 0;
    }
    const totalBorders = game.bordersLand[zoneID].length + game.bordersWater[zoneID].length;
    if(totalBorders === 0) {
      return 0;
    }
    const i = Math.floor(Math.random() * totalBorders);
    if( i < game.bordersLand[zoneID].length ) {
      return game.bordersLand[zoneID][i];
    }
    return game.bordersWater[zoneID][i - game.bordersLand[zoneID].length];
  },

  isCapital: function (game: Game, zoneID: number) {
    // Returns true if the zone is a capital for any player, else false.
    for(let playerIndex = 0; playerIndex <= game.players; playerIndex++)
      if( game.capital[playerIndex] === zoneID )
        return true;
    return false;
  },

  getForceTypeIndexByName: function (game: Game, name: string) {
    // Returns force type index matching name, or -1 for none.
    for(let i = 0, len = game.forceName.length; i < len; i++)
      if( game.forceName[i] === name )
        return i;
    return -1;
  },

  getPlayerForceIndex: function (game: Game, playerIndex: number, forceID: number) {
    // Returns the index of the given force for the player, or -1 if not found.
    const len = game.force[playerIndex].length;
    for(let i = 0; i < len; i++)
      if( game.force[playerIndex][i][BF.FORCE_INDEX_ID] === forceID )
        return i;
    return -1;
  },

  getForce: function (game: Game, forceID: number) {
    // Returns the data array of the given force for the forceID, null if not found.
    for(let playerIndex = 0; playerIndex <= game.players; playerIndex++) {
      const len = game.force[playerIndex].length;
      for(let i = 0; i < len; i++)
        if( game.force[playerIndex][i][BF.FORCE_INDEX_ID] === forceID )
          return game.force[playerIndex][i];
    }
    return null;
  },

  zoneHasPlayerForce: function (
    game: Game, zoneID: number, playerIndex: number, forceTypeID: number,
  ) {
    // Returns 1 if the given zone has at least one force of given type for the player index,
    // else returns 0.
    let len = game.force[playerIndex].length;
    for(let i = 0; i < len; i++)
      if( game.force[playerIndex][i][BF.FORCE_INDEX_ZONE] === zoneID &&
          game.force[playerIndex][i][BF.FORCE_INDEX_TYPE] === forceTypeID )
        return 1;
    return 0;
  },

  zoneOwnerFlag: function (game: Game, zoneID: number) {
    // Returns country code string for zone owner else ''.
    if(zoneID >= 1 && zoneID <= game.zones) {
      return(game.playerFlag[game.zoneOwner[zoneID]]);
    }
    return '';
  },

  zoneOwnerName: function (game: Game, zoneID: number) {
    // Returns the name string for zone owner else ''.
    if(zoneID >= 1 && zoneID <= game.zones) {
      return(game.playerName[game.zoneOwner[zoneID]]);
    }
    return '';
  },

  posXYToInt: function (x: number, y: number): number {
    // Convert x and y positions to a the single value position int.
    return Math.floor(y) * BF.WORLD_WIDTH + Math.floor(x);
  },

  existsUnpickedZone: function (game: Game): boolean {
    // Returns true if at least one zone is still available for picking.
    for(let z = 1; z <= game.zones; z++) {
      if( game.zoneOwner[z] === 0 && isLand(game, z) ) {
        return true;
      }
    }
    return false;
  },

  createNewForce: function (
    game: Game, playerIndex: number, forceType: number,
    zoneID: number, zoneIDPrev: number, x: number, y: number,
  ) {
    // Creates a new force.  Returns the forceID.
    const forceID = game.nextForceID++;
    game.force[playerIndex].push([forceID,
                                  forceType,
                                  zoneID,
                                  zoneIDPrev,
                                  0,   // Retreat zoneID.
                                  game.forceRange[forceType],         // Movement points.
                                  game.forceCargoCapacity[forceType], // Cargo points.
                                  game.forceLiftCapacity[forceType],  // Lift points.
                                  x,
                                  y,
                                  x,
                                  y
                                 ]);
    return forceID;
  },

  playerHasFinished: function (game: Game, uid: number) {
    // Returns true if player for given uid has finished for the given game, else false.
    // If player is not in the game it will also return false.
    for(let p = 1; p < game.playerID.length; p++)
      if(game.playerID[p] === uid &&
          Array.isArray(game.finished) && Array.isArray(game.finished[p]) &&
          game.finished[p][1])
        return true;
    return false;
  },

  eliminatePlayer: function (game: Game, playerIndex: number) {
    // Sets all of a player's zones to neutral and destroys all their remaining forces.
    // NOTE:  Does not set their finishing rank or turn (call setPlayerFinish() for that).

    // Make any indestructable forces for this player be neutral:
    for(let i = game.force[playerIndex].length - 1; i >= 0; i--) {
        const forceType = game.force[playerIndex][i][BF.FORCE_INDEX_TYPE];
        if(!(game.forceFlags[forceType] & (BF.FORCE_FLAG_IS_SURFACE_ONLY_TARGET |
                                           BF.FORCE_FLAG_IS_AIR_ONLY_TARGET))) {
            // Indestructable (such as a factory), so set to neutral:
            const forceID = game.force[playerIndex][i][BF.FORCE_INDEX_ID];
            const newOwner = 0;  // Set to neutral.
            game.force[newOwner].push(game.force[playerIndex][i]);
            game.force[playerIndex].splice(i, 1);
            bfTurnReplay.capture(game, forceID, playerIndex, newOwner);
        }
    }

    // Flag any remaining forces for this player as destroyed:
    for(let i = 0; i < game.force[playerIndex].length; i++) {
        const zoneID = game.force[playerIndex][i][BF.FORCE_INDEX_ZONE];
        if(zoneID >= BF.FORCE_ZONE_PRODUCED || zoneID <= BF.FORCE_ZONE_CARGO) {
            bfTurnReplay.wipe(game, game.force[playerIndex][i][BF.FORCE_INDEX_ID]);
            game.force[playerIndex][i][BF.FORCE_INDEX_ZONE] = BF.FORCE_ZONE_DESTROYED;
        }
    }

    // Set any owned zones to neutral:
    for(let zoneID = 1; zoneID <= game.zones; zoneID++) {
        if( game.zoneOwner[zoneID] === playerIndex ) {
            bfTurnReplay.lose(game, zoneID);
            game.zoneOwner[zoneID] = 0;
        }
    }
  },

  setPlayerFinish: function (game: Game, playerIndex: number, rank: number) {
    game.finished[playerIndex][0] = rank;
    game.finished[playerIndex][1] = game.turn;
  },

  setOrUpdatePlayerFinish: function (game: Game, playerIndex: number, rank: number) {
    if( !game.finished[playerIndex][0] )  // Update finished turn only if rank not yet set.
        game.finished[playerIndex][1] = game.turn;
    game.finished[playerIndex][0] = rank;
  },

  playerName: function (game: Game, playerIndex: number) {
    // Returns the name string or ''.
    if(playerIndex >= 1 && playerIndex <= game.zones) {
      return(game.playerName[playerIndex]);
    }
    return '';
  },

  playerFlag: function (game: Game, playerIndex: number) {
    // Returns the flag string or ''.
    if(playerIndex >= 1 && playerIndex <= game.zones) {
      return(game.playerFlag[playerIndex]);
    }
    return '';
  },



  turnIndexAsString: (turn: number, baseYear: number) => {
    if(turn < BF.TURN_REGISTRATION) {
      return 'PreRegistration';
    }
    if(turn < BF.TURN_PICK_ZONE) {
      return 'Registration';
    }
    if(turn < BF.TURN_PICK_CAPITAL) {
      return 'Zone Picking';
    }
    if(turn < BF.TURN_NORMAL) {
      return 'Capital Selection';
    }
    if(!baseYear) {
      baseYear = 2000;
    }
    return 'Year ' + (baseYear + turn - BF.TURN_NORMAL);
  },

  membershipLevelAsString: (level: number) => {
    if( !level || level <= 1 ) {
      return 'Basic';
    }
    if( level >= 3 ) {
      return 'Extreme';
    }
    return 'Premium';
  },

  msToString: function (ms: number) {
    if(ms <= 0) {
      return '00:00:00';
    }
    let hours = Math.floor(ms / (60*60*1000));
    ms %= 60*60*1000;
    let minutes = Math.floor(ms / (60*1000));
    ms %= 60*1000;
    let seconds = Math.floor(ms / 1000);
    return twoDigits(hours) + ':' + twoDigits(minutes) + ':' + twoDigits(seconds);
  },

  calcDistanceTable: function (game: Game): number[][] {
    // Returns a two dimentional distance table for the game map [zones][zones] = distance.
    // If distance is above FORCE_MAX_MOVES, value will be 1000000.
    const distance: number[][] = [];
    const zones = game.zones;
    for(let j = 0; j <= zones; j++) {
      distance[j] = [];
      for(let k = 0; k <= zones; k++) {
        distance[j][k] = 1000000;   // default to too far.
      }
      distance[j][j] = 0;     // Distance to self is 0, and starting point for scan.
      for(let n = 0; n < BF.FORCE_MAX_MOVES; n++) {
        for(let k = 0; k <= zones; k++) {
          if( distance[j][k] === n ) {
            for(let q = 0; q < game.bordersLand[k].length; q++) {
              if( distance[j][game.bordersLand[k][q]] === 1000000 ) {
                distance[j][game.bordersLand[k][q]] = n + 1;
              }
            }
            for(let q = 0; q < game.bordersWater[k].length; q++) {
              if( distance[j][game.bordersWater[k][q]] === 1000000 ) {
                distance[j][game.bordersWater[k][q]] = n + 1;
              }
            }
          }
        }
      }
    }
    return distance;
  },

};

export default bfH;
